import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { LabFeatureFlags } from 'constants/lab'
import { isLabTakeoverBannerContentPayload } from 'lib/lab/dynamicPayload'
import { useSiteTakeoverTheme } from 'takeovers/utils/hooks/useSiteTakeoverTheme'
import { GenericBackgroundImage } from 'takeovers/utils/components/GenericBackgroundImage'
import { useCurrentSiteTakeoverV2 } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import SiteTakeoverBackgroundImage from './SiteTakeoverBackgroundImage'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import MarkdownRender from 'LuxKit/MarkdownRender'
import Image from 'components/Common/Image'
import Heading, { Heading3Style, Heading3AltStyle } from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import { getThemeClassName } from 'lib/theme/themeUtils'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { stripMarkdownFormatting } from 'lib/string/stripMarkdown'
import useResponsiveSelector from 'hooks/useResponsiveSelector'

const Banner = styled(Group)`
  padding: ${rem(12)} ${rem(20)};
  position: relative;
  width: 100%;

  ${mediaQueryUp.desktop} {
    padding: ${rem(20)} ${rem(150)};
  }
`

const SiteTakeoverBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
`

const SiteTakeoverBackgroundStyle = styled.div<{ backgroundStyle?: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  background: ${props => props.backgroundStyle};
`

const Background = styled(SiteTakeoverBackgroundImage)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
`

const Content = styled(Group)`
  position: relative;
  z-index: 1;
`

const DesktopBreakpoint = styled(CSSBreakpoint)`
  flex: 1;
  width: 100%;
`

// in typography, h3 doesn't support italics so we're overriding it here
const StyledHeading3MarkdownRender = styled(MarkdownRender)`
  line-height: inherit;
  color: inherit;
  ${Heading3Style}

  h3:is(i, em),
  h3 i,
  h3 em {
    ${Heading3AltStyle}
  }
`

interface Props {
  vertical?: App.SiteTakeoverVertical
}

function SiteTakeoverSearchBanner({ vertical }: Props) {
  const siteTakeoverTheme = useSiteTakeoverTheme()
  const customTakeover = useCurrentSiteTakeoverV2({ vertical })

  const enableSiteTakeoverBannerContent = useLabFeatureFlag(LabFeatureFlags.enableTakeoverBannerContent)
  const siteTakeoverBannerContentPayload: App.LabTakeoverBannerContentPayload | undefined = useMemo(() => {
    if (enableSiteTakeoverBannerContent?.enabled) {
      const payload = enableSiteTakeoverBannerContent.value
      if (isLabTakeoverBannerContentPayload(payload)) {
        return payload
      }
    }
    return undefined
  }, [enableSiteTakeoverBannerContent])

  const useLab = !!siteTakeoverTheme && !!siteTakeoverBannerContentPayload
  const useCustom = !useLab && !!customTakeover?.searchPage?.bannerContent

  const shouldHideOnMobile = useCustom && customTakeover?.searchPage?.hideOnMobileScreenSize

  // there are still some custom elements in the best of 2025 design
  // similar logic to isDesktop but its separate for readability
  // TODO @harry-nguyen-le: remove after best of 2025 ends
  const useCustomBestOf2025Elements = useResponsiveSelector({
    mobile: false,
  }) ?? true

  // Extract values for lab config
  const isDesktop = useResponsiveSelector({
    mobile: false,
  }) ?? true
  const locationIcons = siteTakeoverBannerContentPayload?.locationIcons
  const logoConfig = useMemo<App.SiteTakeoverImageConfig | undefined>(() => {
    const locationIcon = locationIcons?.search
    if (locationIcon) {
      const icon = isDesktop ? locationIcon.desktopIcon : locationIcon.mobileIcon
      if (icon) {
        const iconWidth = isDesktop ? locationIcon.desktopIconDimension?.width : locationIcon.mobileIconDimension?.width
        const iconHeight = isDesktop ? locationIcon.desktopIconDimension?.height : locationIcon.mobileIconDimension?.height
        return {
          id: icon,
          width: iconWidth,
          height: iconHeight,
        }
      }
    }
    const fallbackLogoRef = siteTakeoverTheme?.logoRef
    return fallbackLogoRef ? { id: fallbackLogoRef } : undefined
  }, [locationIcons, isDesktop, siteTakeoverTheme?.logoRef])
  const title = siteTakeoverBannerContentPayload?.title
  const subtitle = siteTakeoverBannerContentPayload?.subtitle ? stripMarkdownFormatting(siteTakeoverBannerContentPayload.subtitle) : undefined
  const attachments = siteTakeoverBannerContentPayload?.attachments
  const backgroundRef = siteTakeoverBannerContentPayload?.backgroundRef
  // there are still some custom elements in the best of 2025 design
  // and I dont want to keep creating/updating new lab payloads for them
  // when we are so close to code freeze
  // TODO for 2026: even more custom lab payloads
  const backgroundStyle = (useCustomBestOf2025Elements && siteTakeoverTheme?.id === '2025') ? 'linear-gradient(270deg, rgba(50, 172, 202, 0.10) 0%, #0E839C 80%)' : siteTakeoverBannerContentPayload?.backgroundStyle
  const paletteType = useLab ? (siteTakeoverTheme?.palette ?? 'inverse') : customTakeover?.palette

  const takeOverBackground = (backgroundRef && useCustomBestOf2025Elements)
    ? <GenericBackgroundImage imageId={backgroundRef} backgroundStyle={backgroundStyle} attachments={attachments} />
    : backgroundStyle
      ? <SiteTakeoverBackgroundStyle backgroundStyle={backgroundStyle} />
      : undefined

  // Determine banner content based on config type
  const bannerContent = useLab ? (
    <>
      <CSSBreakpoint max="mobile">
        <Group direction="horizontal" gap={8} verticalAlign="start">
          {logoConfig && <Image id={logoConfig.id} dpr={2} enhancedFormats={false} width={logoConfig.width && logoConfig.height ? logoConfig.width : 32} height={logoConfig.width && logoConfig.height ? logoConfig.height : 32} />}
          <Group direction="vertical" gap={4} verticalAlign="start">
            {title && <Heading variant="heading4">{stripMarkdownFormatting(title)}</Heading>}
            {subtitle && <BodyText variant="medium">{subtitle}</BodyText>}
          </Group>
        </Group>
      </CSSBreakpoint>

      <DesktopBreakpoint min="tablet">
        <Group direction="horizontal" gap={16} verticalAlign="center" fullWidth>
          {logoConfig && <Image id={logoConfig.id} dpr={2} enhancedFormats={false} width={logoConfig.width && logoConfig.height ? logoConfig.width : 60} height={logoConfig.width && logoConfig.height ? logoConfig.height : 60} />}
          <Group direction="vertical" gap={4} verticalAlign="start" fullWidth grow>
            {title && (
              <StyledHeading3MarkdownRender
                // best of: force h3 render
                content={'### ' + title}
                fontSize="large"
              />
            )}
            {subtitle && <BodyText variant="large">{subtitle}</BodyText>}
          </Group>
        </Group>
      </DesktopBreakpoint>
    </>
  ) : customTakeover?.searchPage?.bannerContent

  return (
    <CSSBreakpoint min={shouldHideOnMobile ? 'tablet' : undefined}>
      <Banner direction="horizontal" verticalAlign="center" className={getThemeClassName({ mobile: paletteType })}>
        {useCustom && <Background background="default" vertical={vertical} />}
        {(useLab && takeOverBackground) && <SiteTakeoverBackground>{takeOverBackground}</SiteTakeoverBackground>}
        <Content direction="horizontal" gap={12} verticalAlign="center" fullWidth>
          {bannerContent}
        </Content>
      </Banner>
    </CSSBreakpoint>
  )
}

export default SiteTakeoverSearchBanner
